import React, { useState, useEffect } from 'react';
import { ModalWrapper } from '../modals/ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import emailTemplateService, { EmailTemplate } from '../../services/emailTemplateService';
import { questionnaireService } from '../../services/questionnaireService';
import { QuestionnaireType } from '../../types/questionnaire.types';
import { getApiError } from '../../utils/apiError';

interface SendQuestionnaireToPatientDialogProps {
  open: boolean;
  onClose: () => void;
  /** Ignored when entity is "lead". */
  patientId?: number;
  /** Required when entity is "lead". */
  leadId?: number;
  /**
   * Which side of the wall we are on. A lead is most often sent a
   * create_patient questionnaire - that is what turns them into a patient -
   * so the type list is NOT narrowed to collect_info the way it is for a
   * patient, and the templates come from the LEAD context.
   */
  entity?: 'patient' | 'lead';
  patientName: string;
  tenantId: number;
  onSendSuccess?: (message: string) => void;
  onSendError?: (error: string) => void;
}

const SendQuestionnaireToPatientDialog: React.FC<SendQuestionnaireToPatientDialogProps> = ({
  open,
  onClose,
  patientId,
  leadId,
  entity = 'patient',
  patientName,
  tenantId,
  onSendSuccess,
  onSendError,
}) => {
  const [questionnaireTypes, setQuestionnaireTypes] = useState<QuestionnaireType[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | ''>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setSelectedTypeId('');
      setSelectedTemplateId('');
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entity]);

  const loadData = async () => {
    try {
      setLoading(true);
      const types = await questionnaireService.getQuestionnaireTypes(tenantId);
      const usable = entity === 'lead'
        ? types.filter((t) => t.type === 'collect_info' || t.type === 'create_patient')
        : types.filter((t) => t.type === 'collect_info');
      setQuestionnaireTypes(usable);
      const firstTypeId = usable.length > 0 ? usable[0].id : null;
      if (firstTypeId) {
        setSelectedTypeId(firstTypeId);
        await loadTemplatesForType(firstTypeId);
      }
    } catch (err: any) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplatesForType = async (typeId: number) => {
    try {
      // A lead can be sent the selected questionnaire type's own send template
      // (SEND_QUESTIONNAIRE_<id> - the same one the patient flow uses), a generic
      // Send Questionnaire template, or a lead-specific one. Querying only the
      // lead context missed the per-type templates people actually create.
      const contexts = entity === 'lead'
        ? ['SEND_QUESTIONNAIRE', 'SEND_QUESTIONNAIRE_LEAD', `SEND_QUESTIONNAIRE_${typeId}`]
        : ['SEND_QUESTIONNAIRE', `SEND_QUESTIONNAIRE_${typeId}`];
      const results = await Promise.all(
        contexts.map((ctx) => emailTemplateService.getTemplates(ctx, true)),
      );
      // Deduplicate by id
      const seen = new Set<number>();
      const deduped = results.flat().filter((t) => { if (seen.has(t.id)) return false; seen.add(t.id); return true; });
      setTemplates(deduped);
      if (deduped.length > 0) setSelectedTemplateId(deduped[0].id);
      else setSelectedTemplateId('');
    } catch {
      setTemplates([]);
      setSelectedTemplateId('');
    }
  };

  const handleSend = async () => {
    if (!selectedTypeId || !selectedTemplateId) return;
    try {
      setSending(true);
      setError(null);
      if (entity === 'lead') {
        const { leadService } = await import('../../services/leadService');
        await leadService.sendQuestionnaire(
          Number(leadId), Number(selectedTypeId), Number(selectedTemplateId),
        );
      } else {
        await questionnaireService.sendQuestionnaireToPatient(
          Number(patientId),
          Number(selectedTypeId),
          Number(selectedTemplateId),
          tenantId
        );
      }
      const typeName = questionnaireTypes.find((t) => t.id === selectedTypeId)?.title || '';
      if (onSendSuccess) onSendSuccess(`Questionnaire "${typeName}" sent to ${patientName}`);
      onClose();
    } catch (err: any) {
      const errorMessage = getApiError(err, 'Failed to send questionnaire');
      setError(errorMessage);
      if (onSendError) onSendError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={`Send Questionnaire to ${patientName}`}
      isLoading={sending}
      width="max-w-xl"
      titleAlign="left"
      titleClassName="text-lg font-bold text-[#366A75] leading-tight"
    >
      <div className="space-y-4">
        {error && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700 font-bold">×</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-[#366A75] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {questionnaireTypes.length === 0 ? (
              <div className="px-4 py-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded">
                {entity === 'lead'
                  ? 'No questionnaire types available to send.'
                  : 'No collect_info questionnaire types available.'}
              </div>
            ) : (
              <div>
                <label className="block text-[#366A75] font-medium mb-2">Questionnaire Type</label>
                <select
                  value={selectedTypeId}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    setSelectedTypeId(id);
                    loadTemplatesForType(id);
                  }}
                  disabled={sending}
                  className={`${POPUP_STYLES.input} appearance-none`}
                >
                  {questionnaireTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.title}</option>
                  ))}
                </select>
              </div>
            )}

            {templates.length === 0 ? (
              <div className="px-4 py-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded">
                No active email templates found for "Send Questionnaire" context. Please create one first.
              </div>
            ) : (
              <div>
                <label className="block text-[#366A75] font-medium mb-2">Email Template</label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                  disabled={sending}
                  className={`${POPUP_STYLES.input} appearance-none`}
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {selectedTemplateId && (() => {
                  const tmpl = templates.find((t) => t.id === selectedTemplateId);
                  return tmpl ? (
                    <p className="text-xs text-[#366A75] mt-1">Subject: {tmpl.subject}</p>
                  ) : null;
                })()}
              </div>
            )}
          </>
        )}

        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleSend}
            disabled={sending || !selectedTypeId || !selectedTemplateId || loading}
            className={POPUP_STYLES.button}
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
          <button
            onClick={onClose}
            disabled={sending}
            className={POPUP_STYLES.button}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default SendQuestionnaireToPatientDialog;
